import {React, observer, observable, classNames, useEffect, useLocalStore, Tab} from '@api'
import {Tooltip2} from '@api'

import {TablePagination, TableCell, TableHead, Table, TableBody, TableRow, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Divider, TextField, Tooltip, IconButton, Tabs, Button} from '@material-ui/core'
import {EMPTY_FUNCTION, LibraryTableFooter, useStores, useVipLocalStore, VipLocalStore} from '@modules'
import {WorkspaceLibraryConfig, StoresInstance} from '@stores'
import {removeUndefinedsNested} from '@utils'
import {SubTitleBar, SearchField2, SecondaryButton, TitleBarControls, DuplicateIcon, SearchField2Store} from 'centric-ui-components'
import {computed, action} from 'mobx'
import {useContext} from 'react'
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined'
import {IUpsertInput} from '@stores/elasticsearch'
import * as css from './Library.css'

interface ILibraryProps {
  actions: any
}

export const LibraryItemsListStoreContext = React.createContext<LibraryItemsListStore>(undefined)
export const useLibraryItemsListStore = () => useContext(LibraryItemsListStoreContext)

export class LibraryItemsListStore extends VipLocalStore {
  constructor(stores: StoresInstance,public props: Partial<ILibraryProps>) {
    super(stores)
    this.library = this.workspaces.workspaceConfig.library
  }

  library: WorkspaceLibraryConfig

  @observable data = []
  @observable itemCount: number = null

  @observable.ref searchField: SearchField2Store | null = null
  setSearchRef = r => this.searchField = r
  @observable search: string = ''
  @observable searching = false

  // endregion

  @observable openPopOver = false

  @observable pageIndex = 0

  @observable rowsPerPage = 50

  @observable value = null

  @observable defaultValue = null

  @observable itemId = null

  @observable activeTab = 0

  get queryFields() {
    return ['name']
  }

  @computed get searchTerms() {
    const {es, library: {index}, queryStringEscaped, queryFields, pageIndex, rowsPerPage} = this
    return removeUndefinedsNested(
      {
        sort: {'name': 'asc'},
        query: queryStringEscaped ? {
          bool: {
            must: [
              {
                query_string: {
                  query: `${es.tokenizeQueryString(queryStringEscaped)}*`,
                  fields: queryFields.filter(v => v != 'id' && v != 'images' && v != 'hex'),
                  default_operator: "AND"
                }
              }
            ]
          }
        } : undefined,
        size: (pageIndex + 1) * rowsPerPage,
        from: pageIndex * rowsPerPage,
        _source: ['images', 'hex', 'exId', 'id', 'name', 'createdAt', 'updatedAt']
      })
  }

  @action loadData = async () => {
    const {library: {index}, searchTerms, es} = this
    const {hits: {hits, total: {value: count}}} = await es.search(
      {
        meta: 'LibraryItemsListStore.loadData',
        index: index,
        body: searchTerms
      }, {log: {query: false, result: false}})
    this.data = hits.map(ele => ele._source)
    this.itemCount = count
  }

  /**
   * https://stackoverflow.com/questions/11566838/elastic-search-hyphen-issue-with-term-filter
   */
  @computed get queryStringEscaped() {
    return this.search ? this.search.replace(/[^\w\s]/g, ' ').trim() : ''
  }

  @action refetch = _.debounce(() => {
    this.loadData()
    this.catalog.fetchWorkspaceCounts()
  }, 1000)

  @action onSearch = (e, v) => {
    if (this.search != v) {
      this.pageIndex = 1
      this.search = v
      this.searching = true
    }
  }

  @action handleChange = event => {
    this.value = event.target.value.trim()
  }

  @action handleChangePage = (event, newPage) => {
    this.pageIndex = newPage
  }

  @action handleChangeRowsPerPage = event => {
    this.rowsPerPage = +event.target.value
    this.pageIndex = 0
  }

  @action handlePopOverToggle = data => {
    if (data.type) {
      this.defaultValue = data.name
      this.itemId = data.id
    }
    this.openPopOver = !this.openPopOver
  }

  @action handleOnCancel = () => {
    this.defaultValue = null
    this.value = null
    this.defaultValue = null
    this.openPopOver = false
  }

  @action createItem = () => {
    const {value, library: {index}, es} = this
    let attributeDetails

    //todo - this does not make sense to me NVS - we should have NO hardcoded properties like this in client code.
    switch (index) {
      case 'vip-tag':
        attributeDetails = {
          data: {
            name: value,
            type: {name: 'MerchOptionVersionTag'},
            description: null
          }
        }
        break
      default:
        throw new Error(`Unhandled index - ${index}`)
        break
    }

    const payLoad: IUpsertInput = {
      index: index, value: {
        ...attributeDetails
      }
    }
    this.value = null
    es.upsert(payLoad)
    this.reset()

  }

  @action updateItem = async (e) => {
    const {library: {index}} = this,
    { library: { index: fromIndex } } = this,
    { es, catalog: { workspaceConfig: { _id, library: {
      dragType: {
        entityType, field, index: toIndex, path, source
      }
    } } } } = this
    e.preventDefault()
    e.stopPropagation()
    await es.upsert({
      index: index,
      value: {
        data: {
          name: this.value,
          id: this.itemId
        },
        query: {
          query: {
            match: {
              _id: this.itemId
            }
          }
        }

      }
    })
    await es.mutateField({
      workspaceId: _id,
      entityType: entityType,
      from: {
        filter: {
          query: {
            match: {
              id: this.itemId
            }
          },
          _source: source
        }, index: fromIndex
      },
      pathFromSource: path,
      to: {
        filter: {
          match: {
            [`${field}.id`]: this.itemId
          }
        },
        index: toIndex,
        field: field
      },
      mutationType: 'update'
    })
    this.reset()
  }

  @action reset = () => {
    this.itemId = null,
      this.value = null,
      this.defaultValue = null
    this.openPopOver = false
    this.refetch()
  }

  @action deleteItem = id => async(e) => {
    const { library: { index: fromIndex } } = this,
      { es, catalog: { workspaceConfig: { _id, library: {
        dragType: {
          entityType, field, index: toIndex, path, source
        }
      } } } } = this
    e.preventDefault()
    e.stopPropagation()
   await es.mutateField({
      workspaceId:_id,
      entityType:entityType,
      from:{filter:{
        query:{
          match:{
            id: id
          }
        },
        _source:source
      },index:fromIndex},
      pathFromSource:path,
      to:{
        filter:{
          match:{
            [`${field}.id`]:id
          }
        },
        index:toIndex,
        field:field
      },
      mutationType:'delete'
    })
   await es.delete(id, fromIndex)


    this.reset()
  }

  @action switchTab = event => {
    this.activeTab = event
  }

  @action duplicateItem = name => e => {
    e.preventDefault()
    e.stopPropagation()
    this.value = name
    this.createItem()
  }
}

export const Library = observer<{ className?: string }>(({className}) => {
  const store = useVipLocalStore((ws) => new LibraryItemsListStore(ws, {}))

  useEffect(() => {store.loadData()}, [store.searchTerms])

  return (
    <div className={classNames(css.root, className)}>
      <LibraryItemsListStoreContext.Provider value={store}>
        <TopBar/>
        <LibraryTable/>
        <LibraryPopUp/>
      </LibraryItemsListStoreContext.Provider>
    </div>
  )
})

const LibraryTable = observer(() => {
  return <div className={css.tableRoot}>
    <div className={css.tableWrapper}>
      <Table className={classNames(css.table)}>
        <LibraryHeader/>
        <LibraryBody/>
      </Table>
    </div>
    <LibraryFooter/>
  </div>
})

const LibraryHeader = observer(() => {
  const {library: {tabs}, activeTab} = useLibraryItemsListStore()
  return (
    <TableHead className={css.header}>
      <TableRow>
        {tabs[activeTab].table.columns.map(column => {
          return <TableCell key={column.field} id={column.field} align="left">{column.label}</TableCell>
        })}
      </TableRow>
    </TableHead>
  )
})

const LibraryBody = observer(() => {
  const {pageIndex, rowsPerPage, library: {tabs}, activeTab, handlePopOverToggle, data} = useLibraryItemsListStore()
  return (
    <TableBody className={css.tableBody}>
      {data.slice().map((row, index) => {
        return <TableRow key={row.id} hover onClick={() => handlePopOverToggle({...row, type: 'update'})}>
          {tabs[activeTab].table.columns.map(({field}, i) => {
            const value = ['createdAt', 'updatedAt'].includes(field) ? row[field] ? new Date(row[field]).toLocaleDateString() : '-' : row[field]
            return <TableCell key={i} component="th" scope="row" className={css.cell} data-field={field}>
              {field == 'actions' ?
               <Actions name={row.name}
                        id={row.id}/>
                                  : value}
            </TableCell>
          })}
        </TableRow>
      })}
    </TableBody>
  )
})

const LibraryFooter = observer(() => {
  const {itemCount, rowsPerPage, pageIndex, handleChangePage, handleChangeRowsPerPage} = useLibraryItemsListStore()
  return (
    itemCount !== null && <TablePagination
      rowsPerPageOptions={[50, 100, 150]}
      //component="div"
      count={itemCount}
      rowsPerPage={rowsPerPage}
      page={pageIndex}
      onPageChange={EMPTY_FUNCTION}
      onChangePage={handleChangePage}
      onChangeRowsPerPage={handleChangeRowsPerPage}
      className={classNames(css.footer, css.pager)}
      ActionsComponent={LibraryTableFooter as any}
      classes={{
          caption: css.caption,
          toolbar: css.paginationToolbar,
          select: css.paginationSelect,
          selectRoot: css.selectRoot,
          selectIcon: css.selectIcon
      }}
      //colSpan={Math.ceil(data.headers.length)}
      labelDisplayedRows={e => {
          return <div className={css.displayedRows}>{itemCount === null ? 'Loading...' : `${(pageIndex * rowsPerPage) + 1}-${Math.min((pageIndex * rowsPerPage) + rowsPerPage, itemCount)} of ${itemCount}`}</div>
      }}
    />
  )

    /*

          colSpan={Math.ceil(data.headers.length)}
          labelDisplayedRows={e => {
            return <div
              className={css.displayedRows}>{loading && data.totalResults == null ? 'Loading...' : `${(page * data.rowsPerPage) + 1}-${Math.min((page * data.rowsPerPage) + data.rowsPerPage, data.totalResults)} of ${data.totalResults}`}</div>
          }}

     */
})

const LibraryPopUp = observer(function LibraryPopUp( ) {
  const { openPopOver, handleOnCancel, handleChange, defaultValue, createItem, updateItem, value } = useLibraryItemsListStore(),
    { workspaces: { workspaceConfig } } = useStores(),
    itemName = _.capitalize(workspaceConfig.itemName ?? workspaceConfig.title)
  const labels = defaultValue ? {
    inputName: `Update ${itemName}`, btnText: 'Update', dialogtitle: `Update ${itemName}`
  } : {
    inputName: `New ${itemName}`, btnText: 'Create', dialogtitle: `Create New ${itemName}`
  }

  const action = defaultValue ? updateItem : createItem

  return (
    <Dialog
      aria-labelledby='tag-modal-title'
      aria-describedby='tag-modal-description'
      open={openPopOver}
      className={css.addUpdatePopup}
    >
      <DialogTitle className={css.title} disableTypography>{labels.dialogtitle}</DialogTitle>
      <Divider />
      <DialogContent className={css.content}>
        <div className={css.input}>
          <TextField
            label={`${itemName} Name`}
            defaultValue={defaultValue} placeholder={`Enter ${itemName} Name`} onChange={handleChange} required />
        </div>
      </DialogContent>
      <DialogActions className={css.actions}>
        <Button color='secondary' onClick={handleOnCancel}>CANCEL</Button>
        <Button color='secondary' variant='contained' onClick={action} disabled={_.isEmpty(value) || labels.btnText === 'Update' && value == defaultValue} >{labels.btnText}</Button>
      </DialogActions>
    </Dialog>

  )

})

const TopBar = observer(function TopBar() {
  const {activeTab, library: {tabs}, itemCount, switchTab, onSearch, search, handlePopOverToggle,
  stores: {
    workspaces: {workspaceConfig: {title}}
  }} = useLibraryItemsListStore()

  return (
    <SubTitleBar className={css.titleBar} top={0} position='relative' classes={{toolbar: css.appHeader}}>
      <TitleBarControls classes={{root: css.customTitleBarControls}}>
        <Tabs
          value={activeTab || 0}
          indicatorColor='secondary'
          textColor='secondary'
          classes={{
            root: css.tabRoot
          }}
        >
          {tabs.map((tab, index) => {
            return <Tab
              value={index}
              key={index}
              label={`${tab.label.toUpperCase()} (${itemCount ?? '...'})`}
              classes={{root: css.tabRoot}}
              onClick={() => switchTab(index)}

            />
          })}
        </Tabs>
        <div className={css.spacer}>&nbsp;</div>
        <SearchField2 onSearch={onSearch} value={search} placeholder={`Search ${title}...`}/>
        <SecondaryButton className={css.createNewButton} onClick={handlePopOverToggle}>CREATE NEW</SecondaryButton>
      </TitleBarControls>
    </SubTitleBar>

  )
})

const Actions = observer<{ name: string, id: string }>((props) => {
  const {name, id} = props,
    {strings, deleteItem, duplicateItem} = useLibraryItemsListStore()
  return (
    <div className={css.actions}>
      <Tooltip2 content={strings.duplicate}>
        <IconButton aria-label="Duplicate" onClick={duplicateItem(name)}>
          <DuplicateIcon/>
        </IconButton>
      </Tooltip2>

      <Tooltip2 content={strings.delete}>
        <IconButton aria-label="Delete" onClick={deleteItem(id)}>
          <DeleteOutlinedIcon/>
        </IconButton>
      </Tooltip2>
    </div>
  )
})


// WEBPACK FOOTER //
// ./src/modules/libraries/components/Library.tsx